/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SearchResultsContentDataSource } from './search-results-content-data.source';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { SearchDefinition, SearchFormModel } from '../state/search.model';
import { Select, Store } from '@ngxs/store';
import { AppConfigState } from '../../../core/state/appConfig/app-config.state';
import { SearchState } from '../state/search.state';
import { SearchService } from '../services/search.service';
import { QueryService } from '../../map/services/query.service';
import { LoggerService } from '../../shared/services/logger.service';
import {
    concatMap,
    distinctUntilChanged,
    filter,
    first,
    map,
    shareReplay,
    take,
    takeUntil,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { TableLimits } from '../../../core/state/appConfig/app-config.model';
import { PagingOptions } from '../../../types/PagingOptions';
import { SetSearchPagingOptions } from '../state/search.actions';
import { isEqual } from 'lodash-es';
import { PageState } from '../../../core/state/page/page.state';

@Component({
    selector: 'dash-gis-search-results-content',
    templateUrl: './search-results-content.component.html',
    styleUrls: ['./search-results-content.component.scss'],
})
export class SearchResultsContentComponent implements OnInit, OnDestroy {
    searchName: string;
    searchParams: any;
    dataSource: SearchResultsContentDataSource;
    pageOptions: TableLimits;

    displayedColumns: string[];
    definition: SearchDefinition;
    initialized$: Observable<boolean>;
    criteria$: Observable<SearchFormModel>;

    paging$: Observable<PagingOptions>;

    dispose$: Observable<void>;

    @Input() instanceId: string;

    definition$: Observable<SearchDefinition>;

    protected forPrint = false;
    protected initializedSubject = new BehaviorSubject<boolean>(false);

    @Select(SearchState.currentDefinition) private _definition$: Observable<(string) => SearchDefinition>;
    @Select(SearchState.currentCriteria) private _criteria$: Observable<(string) => SearchFormModel>;
    @Select(SearchState.paging) private _paging$: Observable<(string) => PagingOptions>;

    private _dispose$ = new Subject<void>();

    @ViewChild(MatPaginator) private paginator: MatPaginator;
    @ViewChild('table') private table: MatTable<any>;

    constructor(
        private searchService: SearchService,
        private queryService: QueryService,
        private logger: LoggerService,
        private store: Store
    ) {
        this.initialized$ = this.initializedSubject.asObservable();
        this.dispose$ = this._dispose$.asObservable();

        this.pageOptions = this.store.selectSnapshot(AppConfigState.tableLimits);
        this.definition$ = this._definition$.pipe(map((fn) => fn(this.instanceId)));
        this.criteria$ = this._criteria$.pipe(map((fn) => fn(this.instanceId)));
        this.paging$ = this._paging$.pipe(map((fn) => fn(this.instanceId)));
    }

    ngOnInit() {
        this.definition$
            .pipe(
                tap((definition) => {
                    this.searchName = definition.name;
                    this.definition = definition;
                }),
                first(),
                withLatestFrom(this.criteria$),
                takeUntil(this.dispose$)
            )
            .subscribe(([_, criteria]) => {
                this.searchParams = criteria;
                this.dataSource = new SearchResultsContentDataSource(this.logger, this.searchService, this.forPrint);

                if (this.forPrint) {
                    this.displayedColumns = [];
                    this.definition.resultFields.forEach((col) => {
                        if (col.hasOwnProperty('displayName') && col.displayName) {
                            this.displayedColumns.push(col.name);
                        }
                    });
                    this.paging$
                        .pipe(distinctUntilChanged(isEqual), takeUntil(this.dispose$))
                        .subscribe((pagingOptions) => {
                            this.dataSource.loadAllSearchResults(
                                this.searchName,
                                this.definition,
                                this.searchParams,
                                pagingOptions.sortColumn,
                                pagingOptions.sortDirection
                            );
                        });
                } else {
                    this.displayedColumns = this.mapEnabled ? ['zoom'] : [];

                    this.definition.resultFields.forEach((col) => {
                        if (col.hasOwnProperty('displayName') && col.displayName) {
                            this.displayedColumns.push(col.name);
                        }
                    });
                    this.paging$
                        .pipe(distinctUntilChanged(isEqual), takeUntil(this.dispose$))
                        .subscribe((pagingOptions) => {
                            this.dataSource.loadSearchResults(
                                this.searchName,
                                this.definition,
                                this.searchParams,
                                pagingOptions.page,
                                pagingOptions.limit,
                                pagingOptions.sortColumn,
                                pagingOptions.sortDirection
                            );
                        });
                }
                this.initializedSubject.next(true);
            });
    }

    ngOnDestroy() {
        this._dispose$.next();
    }

    onPage(evt: PageEvent) {
        const options: PagingOptions = {
            page: evt.pageIndex,
            limit: evt.pageSize,
        };
        this.store.dispatch(
            new SetSearchPagingOptions({
                id: this.instanceId,
                reportName: this.definition.name,
                pagingOptions: options,
            })
        );
    }

    onSort(sortEvent: Sort) {
        const options: PagingOptions = {
            sortColumn: sortEvent.active,
            sortDirection: sortEvent.direction,
        };
        this.store.dispatch(
            new SetSearchPagingOptions({
                id: this.instanceId,
                reportName: this.definition.name,
                pagingOptions: options,
            })
        );
    }

    downloadExcel() {
        this.paging$
            .pipe(
                concatMap((paging: PagingOptions) =>
                    this.searchService.runFullSearch(this.searchName, this.searchParams, paging.sortColumn)
                ),
                take(1)
            )
            .subscribe((results) => {
                const excelData = this.searchService.prepareExcelData(results);
                this.searchService.downloadExcel(excelData);
            });
    }

    zoomTo(rowData) {
        this.logger.error('zoomTo');
        const searchDef = this.definition;
        const layerName = rowData.DB_LAYER_ID || searchDef.layer;
        const featureId = rowData.DB_FEATURE_ID;
        this.queryService.zoomTo(layerName, searchDef.objectkey, featureId);
    }

    totalsValue(totals, fieldName) {
        const total = totals.find((t) => t.name === fieldName);
        return total ? total.value : '';
    }

    averagesValue(averages, fieldName) {
        const average = averages.find((a) => a.name === fieldName);
        return average ? average.value : '';
    }

    getTotalsCellDisplay(fieldName: string, index: number): string {
        if (this.definition.totalFields) {
            if (index === 0) {
                return 'label';
            }

            if (-1 !== this.definition.totalFields.findIndex((f) => f === fieldName)) {
                return 'value';
            }
        }
    }

    getAveragesCellDisplay(fieldName: string, index: number): string {
        if (this.definition.averageFields) {
            if (index === 0) {
                return 'label';
            }

            if (-1 !== this.definition.averageFields.findIndex((f) => f === fieldName)) {
                return 'value';
            }
        }
    }

    get printComponentInputs$() {
        return this.criteria$.pipe(
            map((criteria) => ({
                instanceId: this.instanceId,
                params: criteria,
            }))
        );
    }

    get totalsColumnsToDisplay(): string[] {
        return this.displayedColumns.map((name) => `total_${name}`);
    }

    get averagesColumnsToDisplay(): string[] {
        return this.displayedColumns.map((name) => `average_${name}`);
    }

    get mapEnabled() {
        return this.store.selectSnapshot(PageState.mapEnabled);
    }
}
